import { useState, type FormEvent } from 'react'
import { api, ApiError } from '../api.ts'

interface ClientFields {
  id: string
  name: string
  type: string
  nip: string | null
  regon: string | null
  street: string | null
  postalCode: string | null
  city: string | null
  phone: string | null
  email: string | null
  machineryPark: string | null
  tags: string[]
  status: string
}

interface DaneNip {
  name: string
  regon: string | null
  statusVat: string | null
  street: string | null
  postalCode: string | null
  city: string | null
}

/**
 * Full client edit. The quick-add dialog takes a NIP or a name so an offer
 * isn't held up; the rest — address, phone, machinery, tags — is filled in
 * here, later, from the card. Commune/county/voivodeship, acquisition source,
 * orchard area and crops were dropped from the card (W68).
 */
export function EditClientDialog({ client, onClose, onSaved }: { client: ClientFields; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({
    name: client.name,
    type: client.type,
    nip: client.nip ?? '',
    regon: client.regon ?? '',
    street: client.street ?? '',
    postalCode: client.postalCode ?? '',
    city: client.city ?? '',
    phone: client.phone ?? '',
    email: client.email ?? '',
    machineryPark: client.machineryPark ?? '',
    status: client.status,
  })
  const [tags, setTags] = useState(client.tags.join(', '))
  const [error, setError] = useState<string | null>(null)
  const [nipInfo, setNipInfo] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value })
  const nipCyfry = f.nip.replace(/\D/g, '')

  /** Overwrites name and address with the registry's version — that's what the button is for. */
  async function pobierzZNip() {
    setNipInfo(null)
    try {
      const r = await api.get<{ dane: DaneNip | null }>(`/api/clients/nip/${nipCyfry}`)
      if (!r.dane) {
        setNipInfo('Brak w rejestrze VAT — pewnie rolnik ryczałtowy.')
        return
      }
      const d = r.dane
      setF((x) => ({ ...x, name: d.name, regon: d.regon ?? x.regon, street: d.street ?? x.street, postalCode: d.postalCode ?? x.postalCode, city: d.city ?? x.city }))
      setNipInfo(`Pobrano z rejestru VAT (${d.statusVat ?? 'status nieznany'}).`)
    } catch (e) {
      setNipInfo(e instanceof ApiError ? e.message : 'Nie udało się pobrać danych.')
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await api.patch(`/api/clients/${client.id}`, {
        ...f,
        nip: nipCyfry || null,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      })
      onSaved()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Nie udało się zapisać')
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/30 flex items-start justify-center pt-10 pb-10 overflow-y-auto" onClick={onClose}>
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-white rounded-lg shadow-xl border border-slate-200 p-5 space-y-4 my-auto"
      >
        <h2 className="font-bold text-slate-800">Edytuj klienta</h2>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Nazwa *" className="col-span-2">
            <input value={f.name} onChange={set('name')} className={INPUT} />
          </Field>
          <Field label="Status">
            <select value={f.status} onChange={set('status')} className={INPUT}>
              <option value="aktywny">Aktywny</option>
              <option value="potencjalny">Potencjalny</option>
              <option value="nieaktywny">Nieaktywny</option>
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Typ">
            <select value={f.type} onChange={set('type')} className={INPUT}>
              <option value="gospodarstwo">Gospodarstwo</option>
              <option value="firma">Firma</option>
              <option value="osoba_prywatna">Osoba prywatna</option>
            </select>
          </Field>
          <Field label="NIP" hint={nipInfo ?? '10 cyfr, puste u rolnika ryczałtowego'}>
            <div className="flex gap-1">
              <input value={f.nip} onChange={set('nip')} className={`${INPUT} font-mono`} />
              <button
                type="button"
                onClick={() => void pobierzZNip()}
                disabled={nipCyfry.length !== 10}
                className="shrink-0 text-xs px-2 border border-slate-300 rounded hover:border-brand-600 disabled:opacity-40"
                title="Pobierz nazwę i adres z rejestru VAT"
              >
                Pobierz
              </button>
            </div>
          </Field>
          <Field label="REGON">
            <input value={f.regon} onChange={set('regon')} className={`${INPUT} font-mono`} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Telefon">
            <input value={f.phone} onChange={set('phone')} className={INPUT} />
          </Field>
          <Field label="E-mail">
            <input value={f.email} onChange={set('email')} className={INPUT} />
          </Field>
        </div>

        <fieldset className="border-t border-slate-100 pt-3">
          <legend className="text-xs uppercase text-slate-400">Adres</legend>
          <div className="grid grid-cols-4 gap-3 mt-1">
            <Field label="Ulica i nr" className="col-span-2">
              <input value={f.street} onChange={set('street')} className={INPUT} />
            </Field>
            <Field label="Kod">
              <input value={f.postalCode} onChange={set('postalCode')} placeholder="05-660" className={INPUT} />
            </Field>
            <Field label="Miasto">
              <input value={f.city} onChange={set('city')} className={INPUT} />
            </Field>
          </div>
        </fieldset>

        <Field label="Park maszynowy" hint="Co już ma — przydaje się przy ofercie">
          <textarea value={f.machineryPark} onChange={set('machineryPark')} rows={2} className={`${INPUT} resize-y`} />
        </Field>

        <Field label="Tagi" hint="Oddziel przecinkami">
          <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="duży klient, dotacje" className={INPUT} />
        </Field>

        {error && (
          <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex gap-2 pt-1 border-t border-slate-100">
          <button type="button" onClick={onClose} className="text-sm text-slate-500 px-3 py-1.5 hover:text-slate-700">
            Anuluj
          </button>
          <button
            type="submit"
            disabled={busy || !f.name.trim()}
            className="text-sm bg-brand-600 text-white px-4 py-1.5 rounded-md hover:bg-brand-700 disabled:opacity-50"
          >
            {busy ? 'Zapisuję…' : 'Zapisz'}
          </button>
        </div>
      </form>
    </div>
  )
}

const INPUT = 'w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:border-brand-600'

function Field({
  label,
  hint,
  className,
  children,
}: {
  label: string
  hint?: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="block text-sm font-medium text-slate-700 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-xs text-slate-400 mt-0.5">{hint}</span>}
    </label>
  )
}
